import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import "./themeupdate.css";

import { fetchThemeData } from "../../themeSlice";
import {
  selectAdminToken,
  selectOrganizationId,
  selectSessionId,
} from "../../sessionSlice";
import {
  listQuestions,
  saveQuestion,
  deleteQuestion,
  deleteAllQuestions,
  importQuestionsCsv,
} from "../../functions/questionsApi";
import QuizTab from "./QuizTab";
import ThemeUpdateFeatureTour, {
  markThemeUpdateTourSeen,
  shouldAutoStartThemeUpdateTour,
} from "./ThemeUpdateFeatureTour";

const ThemeUpdate = () => {
  const dispatch = useDispatch();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const navigate = useNavigate();
  const { currentTheme, data, status } = useSelector((state) => state.theme);

  const [questions, setQuestions] = useState([]);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [tourOpen, setTourOpen] = useState(false);

  useEffect(() => {
    if (currentTheme) dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
  }, [dispatch, currentTheme]);

  const reloadQuestions = useCallback(async () => {
    if (!currentTheme || !adminToken) return;
    setIsLoadingQuestions(true);
    try {
      const rows = await listQuestions({
        themeName: currentTheme,
        organizationId: organizationId || "admin",
        sessionId: sessionId || "admin",
        token: adminToken,
      });
      setQuestions(rows);
    } catch (err) {
      setErrorMessage(err?.message || "Failed to load questions.");
    } finally {
      setIsLoadingQuestions(false);
    }
  }, [currentTheme, adminToken, organizationId, sessionId]);

  useEffect(() => {
    reloadQuestions();
  }, [reloadQuestions]);

  useEffect(() => {
    if (!data) return;
    if (!shouldAutoStartThemeUpdateTour()) return;
    const t = window.setTimeout(() => setTourOpen(true), 400);
    return () => window.clearTimeout(t);
  }, [data]);

  const closeTour = useCallback(() => {
    markThemeUpdateTourSeen();
    setTourOpen(false);
  }, []);

  const handleSaveQuestion = async ({ action, editId, question, file, removeHint, keepExistingImage }) => {
    setIsSaving(true);
    setErrorMessage("");
    try {
      await saveQuestion({
        themeName: currentTheme,
        organizationId: organizationId || "admin",
        sessionId: sessionId || "admin",
        token: adminToken,
        action,
        editId,
        question,
        file,
        removeHint,
        keepExistingImage,
      });
      await reloadQuestions();
      setSuccessMessage(action === "edit" ? "Question updated." : "Question added.");
    } catch (err) {
      setErrorMessage(err?.message || "Failed to save question.");
      throw err;
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteQuestion = async (id) => {
    setIsSaving(true);
    setErrorMessage("");
    try {
      await deleteQuestion({ themeName: currentTheme, id, token: adminToken });
      await reloadQuestions();
      setSuccessMessage("Question deleted.");
    } catch (err) {
      setErrorMessage(err?.message || "Failed to delete question.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteAll = async () => {
    setIsSaving(true);
    setErrorMessage("");
    try {
      await deleteAllQuestions({ themeName: currentTheme, token: adminToken });
      await reloadQuestions();
      setSuccessMessage("Deleted all questions successfully!");
    } catch (err) {
      setErrorMessage(err?.message || "Failed to delete all questions.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleImportCsv = async (rows) => {
    setIsSaving(true);
    setErrorMessage("");
    try {
      const result = await importQuestionsCsv({ themeName: currentTheme, rows, token: adminToken });
      await reloadQuestions();
      setSuccessMessage(`Successfully updated all fields (${result.total} questions).`);
    } catch (err) {
      setErrorMessage(err?.message || "Failed to import CSV.");
      throw err;
    } finally {
      setIsSaving(false);
    }
  };

  if (status === "loading" && !data) {
    return (
      <div className="themeupdate-page">
        <div className="themeupdate-loading">Loading theme data…</div>
      </div>
    );
  }

  return (
    <div className="themeupdate-page">
      <div className="back-button-holder admin-tour-top-bar">
        <button type="button" className="back-button" onClick={() => navigate("/admin/rules")}>
          <i className="fa-solid fa-arrow-left" /> Back
        </button>
        <button
          type="button"
          className="admin-tour-launch"
          onClick={() => setTourOpen(true)}
          aria-label="Start feature tour"
        >
          <i className="fa-solid fa-route" aria-hidden />
          Take a tour
        </button>
      </div>

      <header className="themeupdate-header themeupdate-panel-wide" data-tour="tour-gc-header">
        <h1 className="themeupdate-title">Game Content</h1>
        <p className="themeupdate-muted">
          Add questions for this theme. Each question has its own options and is stored in the
          questions table.
        </p>
      </header>

      <QuizTab
        questions={questions}
        isLoading={isLoadingQuestions}
        isSaving={isSaving}
        onSaveQuestion={handleSaveQuestion}
        onDeleteQuestion={handleDeleteQuestion}
        onDeleteAll={handleDeleteAll}
        onImportCsv={handleImportCsv}
        errorMessage={errorMessage}
        successMessage={successMessage}
        setErrorMessage={setErrorMessage}
        setSuccessMessage={setSuccessMessage}
      />

      <ThemeUpdateFeatureTour open={tourOpen} onClose={closeTour} />
    </div>
  );
};

export default ThemeUpdate;
